import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { A_LA_CARTE_PRICE } from '@demandproai/shared';
import useAxios from './useAxios';
import useCertifiedLifecarePlan from './useCertifiedLifecarePlan';
import Constants from '../Constants';
import { toaster } from '../utils/toast';

const cost = A_LA_CARTE_PRICE.PRELIMINARY_LIFECARE_PLAN;

const usePreliminaryLifecarePlanRequest = () => {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { getData, postData } = useAxios();
    const { submitLifecarePlan, isLoading: isSubmitting } = useCertifiedLifecarePlan({ caseId });

    const [planner, setPlanner] = useState(null);
    const [isLoadingPlanner, setIsLoadingPlanner] = useState(true);
    const [isRequestingSupport, setIsRequestingSupport] = useState(false);

    const loadPlanner = useCallback(async () => {
        setIsLoadingPlanner(true);
        try {
            const { success, data, message } = await getData(Constants.ApiUrl.certifiedLifecarePlan.getDefaultPlanner);
            if (!success || !data?.planner) {
                toaster({ message: message || 'Failed to load planner', success: false });
                return;
            }
            setPlanner(data.planner);
        } catch (err) {
            toaster({ message: err?.message || 'Failed to load planner', success: false });
        } finally {
            setIsLoadingPlanner(false);
        }
    }, [getData]);

    useEffect(() => {
        loadPlanner();
    }, [loadPlanner]);

    const handleClose = () => navigate(`/case/${caseId}/preliminary-lifecare-plan`);

    const confirmRequest = async () => {
        await submitLifecarePlan();
        handleClose();
    };

    const requestSupport = async () => {
        setIsRequestingSupport(true);
        try {
            const route = `${location.pathname}${location.search}`;
            const { success, message } = await postData(Constants.ApiUrl.certifiedLifecarePlan.requestSupport, { caseId, route });
            toaster({
                message: success ? 'Request sent. Our support team will reach out to you shortly.' : (message || 'Failed to send request'),
                success: Boolean(success),
            });
        } catch (err) {
            toaster({ message: err?.message || 'Failed to send request', success: false });
        } finally {
            setIsRequestingSupport(false);
        }
    };

    return {
        planner,
        isLoadingPlanner,
        cost,
        costLabel: `$${cost.toFixed(2)}`,
        isSubmitting,
        confirmRequest,
        isRequestingSupport,
        requestSupport,
        handleClose,
    };
};

export default usePreliminaryLifecarePlanRequest;
